// components/CommandPalette.jsx
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Package,
  Store,
  TrendingUp,
  Home,
  ShoppingCart,
  User,
  Wallet,
  ClipboardList,
  ShoppingBag,
  Settings,
  Clock,
  Truck,
  CornerDownLeft,
} from "lucide-react";
import { useAuth } from "../providers/AuthProvider";
import { useTheme } from "../hooks/useTheme";

// Static pages — always searchable
const NAV_SHORTCUTS = [
  { path: "/", label: "Home", icon: Home, keywords: "start landing" },
  { path: "/data", label: "Data services", icon: TrendingUp, keywords: "airtime bundles mtn telecel airteltigo" },
  { path: "/marketplace", label: "Marketplace", icon: Store, keywords: "shop products store" },
  { path: "/dropshipping", label: "Ready to ship", icon: Truck, keywords: "in stock dropship instant" },
  { path: "/preorder", label: "Pre-order windows", icon: Clock, keywords: "group buy campaign" },
  { path: "/cart", label: "Cart", icon: ShoppingCart, keywords: "basket checkout" },
  { path: "/orders", label: "Data orders", icon: ClipboardList, keywords: "purchases history" },
  { path: "/marketplace/orders", label: "Marketplace orders", icon: ShoppingBag, keywords: "shop orders history" },
];

const ACCOUNT_SHORTCUTS = [
  { path: "/profile", label: "My profile", icon: User },
  { path: "/wallet", label: "Wallet", icon: Wallet },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function CommandPalette({ open, onClose }) {
  const { colors } = useTheme();
  const { authFetch, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIndex(0);
      // Fetch once per open, cached in state
      if (products.length === 0) {
        authFetch("/marketplace/products/?page_size=200")
          .then((r) => r.json())
          .then((d) => setProducts(d.data || []))
          .catch(() => {});
      }
      if (packages.length === 0) {
        authFetch("/data/packages/")
          .then((r) => r.json())
          .then((d) => setPackages(d.data || []))
          .catch(() => {});
      }
    }
  }, [open, authFetch, products.length, packages.length]);

  // Focus input on open
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 30);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // ── Build the result list ─────────────────────────────
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const groups = [];

    // 1. Navigation shortcuts
    const navMatches = NAV_SHORTCUTS.filter((item) =>
      !q
        ? true
        : item.label.toLowerCase().includes(q) ||
          (item.keywords || "").toLowerCase().includes(q)
    );
    if (navMatches.length > 0) {
      groups.push({
        title: q ? "Pages" : "Jump to",
        items: navMatches.slice(0, q ? 6 : 8).map((item) => ({
          kind: "nav",
          key: `nav-${item.path}`,
          label: item.label,
          icon: item.icon,
          onSelect: () => {
            navigate(item.path);
            onClose();
          },
        })),
      });
    }

    // 2. Products
    if (q.length >= 1) {
      const productMatches = products
        .filter(
          (p) =>
            p.name?.toLowerCase().includes(q) ||
            p.category_name?.toLowerCase().includes(q) ||
            p.short_description?.toLowerCase().includes(q)
        )
        .slice(0, 6);
      if (productMatches.length > 0) {
        groups.push({
          title: "Products",
          items: productMatches.map((p) => ({
            kind: "product",
            key: `prod-${p.id}`,
            label: p.name,
            sublabel: p.category_name || "Marketplace",
            icon: Package,
            thumbnail:
              p.thumbnail_image_url ||
              p.main_image_url ||
              p.main_image ||
              null,
            onSelect: () => {
              navigate(`/marketplace/products/${p.id}`);
              onClose();
            },
          })),
        });
      }

      // 3. Data packages
      const pkgMatches = packages
        .filter(
          (p) =>
            p.network?.toLowerCase().includes(q) ||
            `${p.size_gb}gb`.includes(q) ||
            `${p.size_gb} gb`.includes(q)
        )
        .slice(0, 5);
      if (pkgMatches.length > 0) {
        groups.push({
          title: "Data packages",
          items: pkgMatches.map((p) => ({
            kind: "pkg",
            key: `pkg-${p.id}`,
            label: `${p.network} ${p.size_gb}GB`,
            sublabel: `₵${Number(
              p.effective_price || p.base_price_customer || 0
            ).toFixed(2)}`,
            icon: TrendingUp,
            onSelect: () => {
              navigate(`/data/purchase/${p.id}`);
              onClose();
            },
          })),
        });
      }

      // 4. Account shortcuts
      if (isAuthenticated) {
        const acctMatches = ACCOUNT_SHORTCUTS.filter((item) =>
          item.label.toLowerCase().includes(q)
        );
        if (acctMatches.length > 0) {
          groups.push({
            title: "Your account",
            items: acctMatches.map((item) => ({
              kind: "account",
              key: `acct-${item.path}`,
              label: item.label,
              icon: item.icon,
              onSelect: () => {
                navigate(item.path);
                onClose();
              },
            })),
          });
        }
      }
    }

    return groups;
  }, [query, products, packages, isAuthenticated, navigate, onClose]);

  // Flatten for keyboard navigation
  const flatItems = useMemo(
    () => results.flatMap((g) => g.items),
    [results]
  );

  // Reset active index when the query or items change
  useEffect(() => {
    setActiveIndex(0);
  }, [query, flatItems.length]);

  // ── Keyboard navigation ───────────────────────────────
  const onKeyDown = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % Math.max(flatItems.length, 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(
          (i) => (i - 1 + flatItems.length) % Math.max(flatItems.length, 1)
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        const item = flatItems[activeIndex];
        if (item) item.onSelect();
      }
    },
    [flatItems, activeIndex]
  );

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(
      `[data-index="${activeIndex}"]`
    );
    if (el) el.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  if (!open) return null;

  return (
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={styles.overlay}
    >
      <div style={styles.palette(colors)} onKeyDown={onKeyDown}>
        {/* Search input */}
        <div style={styles.inputRow(colors)}>
          <Search size={17} color={colors.secondaryText} />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search products, packages, or jump to a page…"
            style={styles.input(colors)}
          />
          <kbd style={styles.kbd(colors)}>Esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} style={styles.results}>
          {results.length === 0 ? (
            <div style={styles.empty(colors)}>
              <Search size={20} />
              <div style={styles.emptyTitle(colors)}>No results</div>
              <div style={styles.emptySub(colors)}>
                Try a different search term.
              </div>
            </div>
          ) : (
            results.map((group) => (
              <div key={group.title} style={styles.group}>
                <div style={styles.groupTitle(colors)}>{group.title}</div>
                {group.items.map((item) => {
                  const flatIndex = flatItems.indexOf(item);
                  const active = flatIndex === activeIndex;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      data-index={flatIndex}
                      type="button"
                      onMouseEnter={() => setActiveIndex(flatIndex)}
                      onClick={item.onSelect}
                      style={styles.resultItem(colors, active)}
                    >
                      {item.thumbnail ? (
                        <img
                          src={item.thumbnail}
                          alt=""
                          style={styles.resultThumb}
                        />
                      ) : (
                        <span style={styles.resultIcon(colors, active)}>
                          <Icon size={14} />
                        </span>
                      )}
                      <div style={styles.resultText}>
                        <div style={styles.resultLabel(colors)}>
                          {item.label}
                        </div>
                        {item.sublabel && (
                          <div style={styles.resultSub(colors)}>
                            {item.sublabel}
                          </div>
                        )}
                      </div>
                      {active && (
                        <span style={styles.enterHint(colors)}>
                          <CornerDownLeft size={12} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer(colors)}>
          <FooterHint keys="↑ ↓" label="navigate" colors={colors} />
          <FooterHint keys="↵" label="open" colors={colors} />
          <FooterHint keys="Esc" label="close" colors={colors} />
        </div>
      </div>
    </div>
  );
}

const FooterHint = ({ keys, label, colors }) => (
  <span style={styles.footerHint(colors)}>
    <kbd style={styles.footerKbd(colors)}>{keys}</kbd>
    {label}
  </span>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(10, 15, 30, 0.45)",
    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
    display: "flex",
    justifyContent: "center",
    paddingTop: "12vh",
    zIndex: 1000,
    animation: "paletteFade 0.15s ease",
  },
  palette: (c) => ({
    width: "min(640px, 92vw)",
    maxHeight: "72vh",
    background: c.raisedBg,
    borderRadius: 16,
    border: `1px solid ${c.border}`,
    boxShadow: `0 24px 64px rgba(0,0,0,0.35)`,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    animation: "paletteRise 0.18s ease",
  }),
  inputRow: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "14px 18px",
    borderBottom: `1px solid ${c.border}`,
    flexShrink: 0,
  }),
  input: (c) => ({
    flex: 1,
    minWidth: 0,
    border: "none",
    background: "transparent",
    color: c.text,
    fontSize: 15,
    outline: "none",
    fontFamily: "inherit",
  }),
  kbd: (c) => ({
    fontSize: 10,
    color: c.secondaryText,
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    padding: "2px 6px",
    borderRadius: 5,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
    fontWeight: 600,
  }),
  results: {
    flex: 1,
    overflowY: "auto",
    padding: "6px 8px 12px",
  },
  group: {
    marginBottom: 4,
  },
  groupTitle: (c) => ({
    fontSize: 10,
    fontWeight: 800,
    letterSpacing: 0.8,
    textTransform: "uppercase",
    color: c.secondaryText,
    padding: "10px 10px 6px",
  }),
  resultItem: (c, active) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "9px 10px",
    borderRadius: 9,
    border: "none",
    background: active ? c.hoverBg : "transparent",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    transition: "background 0.08s ease",
  }),
  resultIcon: (c, active) => ({
    width: 30,
    height: 30,
    borderRadius: 8,
    background: active ? c.btnBg : c.subtleBg || c.hoverBg,
    color: active ? c.btnText : c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    transition: "background 0.08s ease, color 0.08s ease",
  }),
  resultThumb: {
    width: 30,
    height: 30,
    borderRadius: 8,
    objectFit: "cover",
    flexShrink: 0,
  },
  resultText: {
    flex: 1,
    minWidth: 0,
  },
  resultLabel: (c) => ({
    fontSize: 13.5,
    fontWeight: 600,
    color: c.heading,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  resultSub: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
    marginTop: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  enterHint: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 22,
    height: 22,
    borderRadius: 6,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    color: c.secondaryText,
    flexShrink: 0,
  }),
  empty: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "48px 24px",
    color: c.secondaryText,
    textAlign: "center",
    gap: 8,
  }),
  emptyTitle: (c) => ({
    fontSize: 14,
    fontWeight: 700,
    color: c.heading,
    marginTop: 4,
  }),
  emptySub: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
  }),
  footer: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "10px 18px",
    borderTop: `1px solid ${c.border}`,
    background: c.subtleBg || c.hoverBg,
    fontSize: 11,
    color: c.secondaryText,
    flexShrink: 0,
  }),
  footerHint: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: c.secondaryText,
  }),
  footerKbd: (c) => ({
    fontSize: 9.5,
    fontWeight: 700,
    color: c.text,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    padding: "1px 5px",
    borderRadius: 4,
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  }),
};

// Keyframe injection
if (typeof window !== "undefined" && !document.getElementById("command-palette-anim")) {
  const el = document.createElement("style");
  el.id = "command-palette-anim";
  el.textContent = `
    @keyframes paletteFade {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes paletteRise {
      from { opacity: 0; transform: translateY(-8px) scale(0.985); }
      to { opacity: 1; transform: translateY(0) scale(1); }
    }
  `;
  document.head.appendChild(el);
}